"use client"

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import Cookies from 'js-cookie';
import { useSearchParams } from 'next/navigation';
import { useState, useEffect } from 'react';

// lib files
import { formatAmount } from '@/lib/helpers/format-amount';
import { sendPbsGtmEvent } from '@/lib/helpers/send-pbs-gtm-event';

// types
import { StationData } from '@/lib/types/api/stations-data';

// constants
import {
  DONATE_FALLBACK_URL,
  HIDE_PASSPORT_PROMPT_COOKIE,
  HIDE_PASSPORT_PROMPT_COOKIE_AGE_DAYS,
  PBS_COOKIE_DOMAIN,
} from '@/lib/constants';

// components
import Button from '@/components/Button/Button';
import LinkButton from '@/components/Button/LinkButton';
import Modal from '@/components/Modal/Modal';

// svg's
import CompassRoseCorner from '@/public/svg/compass-rose-corner.svg';
import HeartIcon from '@/public/svg/donate-heart.svg';

// styles
import styles from './PassportPrompt.module.scss'

export interface PassportPromptProps {
  isOpen?: boolean;
  stationData: StationData;
}

// For Analytics
const PASSPORT_PROMPT_FEATURE_CATEGORY = 'passport prompt';

function PassportPrompt(props: PassportPromptProps) {
  const { isOpen: isOpenProp, stationData } = props;

  const {
    attributes: {
      annual_passport_qualifying_amount,
      passport_enabled,
      passport_url,
      short_common_name,
    }
  } = stationData;

  const donateUrl = passport_url ? passport_url : DONATE_FALLBACK_URL;

  if (passport_enabled === false || annual_passport_qualifying_amount === null) {
    return null;
  }

  const annualAmount = formatAmount(annual_passport_qualifying_amount);
  const monthlyAmount = formatAmount(annual_passport_qualifying_amount / 12);

  const [isOpen, setIsOpen] = useState(!!isOpenProp)
  const searchParams = useSearchParams()
  const showPassportPromptParam = searchParams?.get('showPassportPrompt');

  useEffect(() => {
    // If isOpenProp is explicity set, use that value and ignore the query param
    if (isOpenProp === undefined) {
      // Check for the showPassportPrompt query param
      // Note: for ease of testing, this will override the hidePassportPrompt cookie
      if (showPassportPromptParam === 'true') {
        setIsOpen(true);
        // Remove the parameter from the URL
        const params = new URLSearchParams(searchParams.toString());
        params.delete('showPassportPrompt');
        const newParams = params.toString();
        // And also remove the parameter from the history
        const newUrl = window.location.pathname + (newParams ? '?' + newParams : '');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [searchParams, isOpenProp, showPassportPromptParam])

  const closePassportPrompt = () => {
    setIsOpen(false)
    // When user dismisses the prompt, a cookie is set to hide prompt for 30 days
    const domain = window.pbsPlatform === 'svp' ? window.location.hostname : PBS_COOKIE_DOMAIN;
    Cookies.set(
      HIDE_PASSPORT_PROMPT_COOKIE,
      'true',
      {
        domain: domain,
        path: '/',
        expires: HIDE_PASSPORT_PROMPT_COOKIE_AGE_DAYS,
        sameSite: 'none',
        secure: true,
      }
    );
  }

  const handleContinueWithoutPassportClick = () => {
    closePassportPrompt();
    sendPbsGtmEvent({
      feature_category: PASSPORT_PROMPT_FEATURE_CATEGORY,
      feature_name: "continue without passport button",
      object_action: "click",
      object_action_behavior: "dismisses passport prompt",
      object_name: "continue without passport button",
      object_text: "Continue Without Passport",
      object_type: "button"
    });
  }

  const handleGetPassportClick = () => {
    sendPbsGtmEvent({
      feature_category: PASSPORT_PROMPT_FEATURE_CATEGORY,
      feature_name: "get passport link",
      object_action: "click",
      object_action_behavior: "navigate to passport donation page",
      object_name: "donate link",
      object_text: "Get Passport",
      object_type: "link",
      object_url: donateUrl
    });
  }

  const onModalCloseButtonClick = () => {
    closePassportPrompt();
    sendPbsGtmEvent({
      feature_category: PASSPORT_PROMPT_FEATURE_CATEGORY,
      feature_name: "dismiss button",
      object_action: "click",
      object_action_behavior: "dismisses passport prompt",
      object_name: "dismiss button",
      object_type: "button"
    });
  }

  const onModalBackgroundClick = () => {
    closePassportPrompt();
    sendPbsGtmEvent({
      feature_category: PASSPORT_PROMPT_FEATURE_CATEGORY,
      feature_name: "outside modal area",
      object_action: "click",
      object_action_behavior: "dismisses passport prompt",
      object_name: "modal background",
      object_text: "none",
      object_type: "modal"
    });
  }

  const onModalEscapeKeyPress = () => {
    closePassportPrompt();
    sendPbsGtmEvent({
      feature_category: PASSPORT_PROMPT_FEATURE_CATEGORY,
      feature_name: "user keyboard escape key",
      object_action: "click",
      object_action_behavior: "dismisses passport prompt",
      object_name: "none",
      object_text: "none",
      object_type: "none",
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      className={styles.passport_prompt}
      innerClassName={styles.passport_prompt__inner}
      onCloseButtonClick={onModalCloseButtonClick}
      onModalBackgroundClick={onModalBackgroundClick}
      onEscapeKeyPress={onModalEscapeKeyPress}
    >
      <h2>Discover More from PBS</h2>
      <div className={styles.passport_prompt__content_cards}>
        <div className={styles.passport_prompt__content_card}>
          <div className={styles.passport_prompt__content_card_inner}>
            <h3>Watch Without {short_common_name} Passport</h3>
            <ul className={styles.passport_prompt__list}>
              <li>Stream the latest episodes of your favorite shows on PBS.org and the PBS app</li>
              <li>Enjoy Live TV from {short_common_name}</li>
              <li>Catch up on news and public affairs</li>
            </ul>
            <Button
              className={styles.passport_prompt__continue_without_passport_button}
              onClick={handleContinueWithoutPassportClick}
            >
              Continue Without Passport
            </Button>
          </div>
        </div>
        <div className={styles.passport_prompt__content_card}>
          <span className={styles.passport_prompt__badge}>
            <CompassRoseCorner />
          </span>
          <div className={styles.passport_prompt__content_card_inner}>
            <h3>Watch MORE with {short_common_name} Passport</h3>
            <p className={styles.passport_prompt__donation_text}>Donate ${monthlyAmount} per month or ${annualAmount} per year and:</p>
            <ul className={styles.passport_prompt__list}>
              <li>Get extended access to even more episodes</li>
              <li>Watch full seasons of most popular shows</li>
              <li>Stream early releases and other exclusive content</li>
            </ul>
            <p className={styles.passport_prompt__donation_heart}>
              <HeartIcon/> Your donation goes to your local station!
            </p>
            <LinkButton
              href={donateUrl}
              style="light_blue"
              onClick={handleGetPassportClick}
            >
              Get Passport
            </LinkButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default PassportPrompt
